/**
 * Central route classification for middleware and client AuthGuard.
 * Middleware uses cookies only; AuthGuard uses supabase.auth.getSession() after bootstrap.
 *
 * Redirect contract — the single source of truth for "where can a logged-in user go":
 *
 *   • AUTH_GUEST_ONLY_ROUTES        → bounced to /dashboard or /admin/dashboard when signed in.
 *   • PUBLIC_INFORMATIONAL_ROUTES   → ALWAYS open. Signed-in users see normal content; the auth
 *                                     stack is forbidden from redirecting away from these.
 *   • PROTECTED_USER_ROUTE_PREFIXES → require a session; anon users bounced to /login.
 *
 * The three sets are mutually exclusive by design. If you add a new route, classify it into
 * exactly one bucket so the middleware + AuthGuard agree on the redirect outcome.
 */

/** Logged-in users are redirected away from these (see middleware + AuthGuard). */
export const AUTH_GUEST_ONLY_ROUTES = [
  "/login",
  "/signup",
] as const;

/** Require a valid session (cookie in middleware; session on client). */
export const PROTECTED_USER_ROUTE_PREFIXES = [
  "/dashboard",
  "/profile",
  "/bookings",
  "/messages",
  "/onboarding",
  "/favorites",
  "/settings",
  "/notifications",
  "/rentals",
  "/payment",
  "/listings/create",
] as const;

/**
 * Public informational / marketing / legal pages.
 *
 * Signed-in users MUST be able to browse these without being bounced to a dashboard.
 * This is the allowlist consumed by middleware (`isPublicInformationalRoute`) to skip
 * any "logged-in → dashboard" redirect, and by AuthGuard to refuse to redirect when a
 * stray guard ever wraps one of these pages.
 *
 * Every entry here is also implicitly a public route (no session required). Keep this
 * list explicit — wildcards or "everything not in protected" would break the next time
 * a new authenticated feature is added.
 */
export const PUBLIC_INFORMATIONAL_ROUTES = [
  "/about",
  "/about-us",
  "/contact",
  "/contact-us",
  "/how-it-works",
  "/faq",
  "/help",
  "/help-support",
  "/trust-safety",
  "/trust-safety-community",
  "/terms",
  "/terms-and-conditions",
  "/privacy-policy",
  "/policy-privacy",
  "/refund-policy",
  "/community-guidelines",
  "/safety-guidelines",
] as const;

export const PUBLIC_ROUTE_PREFIXES = [
  "/",
  "/browse",
  "/listings",
  "/host",
  ...PUBLIC_INFORMATIONAL_ROUTES,
  "/auth",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/login",
  "/signup",
  "/mobile",
] as const;

export function pathnameMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

export function isAuthGuestOnlyRoute(pathname: string): boolean {
  return AUTH_GUEST_ONLY_ROUTES.some((p) => pathnameMatchesPrefix(pathname, p));
}

/** /listings/[id]/edit — not covered by /listings public browse. */
export function isListingEditRoute(pathname: string): boolean {
  return /^\/listings\/[^/]+\/edit(\/|$)/.test(pathname);
}

export function isProtectedUserRoute(pathname: string): boolean {
  if (pathname.startsWith("/admin")) return false;
  if (isListingEditRoute(pathname)) return true;
  return PROTECTED_USER_ROUTE_PREFIXES.some((p) => pathnameMatchesPrefix(pathname, p));
}

export function isPublicRoutePath(pathname: string): boolean {
  if (pathnameMatchesPrefix(pathname, "/listings/create")) return false;
  if (isListingEditRoute(pathname)) return false;
  return PUBLIC_ROUTE_PREFIXES.some((p) => pathnameMatchesPrefix(pathname, p));
}

/**
 * True for marketing / legal / informational pages that must remain reachable while
 * signed in. Distinct from `isPublicRoutePath` (which also covers `/login`, `/signup`,
 * `/`, `/browse` — paths where logged-in users DO get redirected or shown different
 * content). Use this in the middleware + AuthGuard "should I bounce a logged-in user?"
 * decision to guarantee informational pages are never redirected.
 */
export function isPublicInformationalRoute(pathname: string): boolean {
  return PUBLIC_INFORMATIONAL_ROUTES.some((p) => pathnameMatchesPrefix(pathname, p));
}
